/**
 * <s-section-nav>
 * Floating glass jump nav (templates/list-collections.liquid). Watches the
 * sections its [data-section-link] anchors point to, marks the one in view
 * with aria-current="true" and slides [data-section-indicator] (the dark pill)
 * under that link. Clicks scroll smoothly (instantly for reduced motion).
 * Also the homepage carousel jump nav (blocks/carousel-zone.liquid): optional [data-section-progress]
 * lines fill with the scroll progress through each section; [data-follow-header] keeps the nav below
 * the sticky header while it is shown.
 */
class SSectionNav extends HTMLElement {
  #ac = null;
  #observer = null;
  #links = [];
  #visible = new Map();
  #progress = [];
  #sections = [];
  #header = null;
  #frame = 0;

  connectedCallback() {
    this.#links = [...this.querySelectorAll('[data-section-link]')];
    const sections = this.#links
      .map((link) => document.getElementById(decodeURIComponent(new URL(link.href, location.href).hash.slice(1))))
      .filter(Boolean);
    if (!sections.length) return;

    this.#ac = new AbortController();
    const { signal } = this.#ac;

    this.#observer = new IntersectionObserver((entries) => {
      for (const entry of entries) this.#visible.set(entry.target.id, entry.intersectionRatio);
      this.#pick();
    }, { rootMargin: '-30% 0px -50% 0px', threshold: [0, 0.01, 0.25, 0.5, 0.75, 1] });
    sections.forEach((section) => this.#observer.observe(section));

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.#links.forEach((link) => {
      link.addEventListener('click', (event) => {
        const target = document.getElementById(decodeURIComponent(new URL(link.href, location.href).hash.slice(1)));
        if (!target) return;
        event.preventDefault();
        target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
        history.replaceState(null, '', link.hash);
        this.#activate(link);
      }, { signal });
    });

    window.addEventListener('resize', () => this.#activate(this.#links.find((l) => l.getAttribute('aria-current') === 'true')), { passive: true, signal });

    // Optional (homepage carousels): a [data-section-progress] line per link fills with the scroll progress
    // through its section, and with [data-follow-header] the nav slides below the sticky header when it shows.
    this.#progress = this.#links.map((link) => link.querySelector('[data-section-progress]'));
    this.#sections = this.#links.map((link) => document.getElementById(decodeURIComponent(new URL(link.href, location.href).hash.slice(1))));
    this.#header = this.hasAttribute('data-follow-header') ? document.querySelector('header') : null;
    if (this.#progress.some(Boolean) || this.#header) {
      const schedule = () => {
        cancelAnimationFrame(this.#frame);
        this.#frame = requestAnimationFrame(() => this.#tick());
      };
      window.addEventListener('scroll', schedule, { passive: true, signal });
      window.addEventListener('resize', schedule, { passive: true, signal });
      this.#header?.addEventListener('transitionend', schedule, { signal });
      this.#tick();
    }
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
    cancelAnimationFrame(this.#frame);
  }

  #tick() {
    const viewport = window.innerHeight;
    this.#sections.forEach((section, index) => {
      const line = this.#progress[index];
      if (!section || !line) return;
      const rect = section.getBoundingClientRect();
      const span = Math.max(1, rect.height - viewport);
      const value = Math.min(1, Math.max(0, -rect.top / span));
      line.style.scale = `${value} 1`;
    });
    if (this.#header) {
      const bottom = Math.max(0, this.#header.getBoundingClientRect().bottom);
      this.style.top = `${Math.round(bottom + 12)}px`;
    }
  }

  #pick() {
    let best = null;
    let bestRatio = 0;
    for (const link of this.#links) {
      const id = decodeURIComponent(new URL(link.href, location.href).hash.slice(1));
      const ratio = this.#visible.get(id) ?? 0;
      if (ratio > bestRatio) {
        best = link;
        bestRatio = ratio;
      }
    }
    this.#activate(best);
  }

  #activate(link) {
    const indicator = this.querySelector('[data-section-indicator]');
    this.#links.forEach((item) => item.setAttribute('aria-current', String(item === link)));
    if (!indicator) return;
    if (!link) {
      indicator.style.opacity = '0';
      return;
    }
    // offsetLeft is physical and relative to the nav (also when the nav scrolls sideways), so the pill is anchored with left-0.
    indicator.style.width = `${link.offsetWidth}px`;
    indicator.style.translate = `${link.offsetLeft}px 0`;
    indicator.style.opacity = '1';
    // On narrow screens the nav scrolls sideways: keep the active link in view.
    const nav = link.parentElement;
    if (nav && nav.scrollWidth > nav.clientWidth) nav.scrollTo({ left: link.offsetLeft - (nav.clientWidth - link.offsetWidth) / 2, behavior: 'smooth' });
  }
}

if (!customElements.get('s-section-nav')) customElements.define('s-section-nav', SSectionNav);
